import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react'

import { useNavBlocker } from 'commons/customHooks/useNavBlocker'
import { formatDate, getLastDayOfMonth } from 'commons/utils/date'
import { isObjectEmpty } from 'commons/utils/object'
import { getUser } from 'commons/utils/user'
import {
  InternationalSampleHeader,
  NationalSampleHeader,
  type OptionProps,
  OVOrderHeader,
  ToolBar,
} from 'components'
import { availableCentersFactories, OVOrder, TypesValidations } from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import type { Cliente } from 'container/CreateOrder'
import { SampleHeaderWrapper } from 'container/CreateOrder/styles'
import type { ClientsListInterface } from 'container/SalesOrder/components/ModalPostOn'
import type { QuotaMonth } from 'container/SalesOrder/interfaces'
import { useFormContext } from 'react-hook-form'
import { useMatch, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getClientCredit } from 'services/client/credit'
import type { ClientCrediInfo } from 'services/client/credit/interfaces'
import { getClientOvTypes } from 'services/client/ovTypes'
import { getLastHeader, getPaymentsTerms, postMaintainDraft } from 'services/draft'
import { getActiveQuotaMonths } from 'services/quota_month'
import { getClientAddress } from 'services/salesOrder'
import {
  asyncGetCorpoRascunho,
  getItems,
  setNumberDraft,
} from 'store/SalesOrderContext/salesOrderSlice'
import { setConfirmedNavigation, setShowPrompt } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import { handleDiscountInput } from './utils'

interface TopSectionProps {
  setLoading?: Dispatch<SetStateAction<boolean>>
  quotasMonth?: QuotaMonth[]
  defaultPayment?: string
  defaultClient?: Pick<
    Cliente,
    | 'cd_clie'
    | 'nm_clie'
    | 'cd_empr'
    | 'cd_orgn_vend'
    | 'cd_canl_dstb'
    | 'cd_seto_atvd'
    | 'cd_regi_vend'
    | 'ds_regi_vend'
    | 'nr_cnpj_cpf'
    | 'cd_escr_vend'
  >
  orderNumber?: string
  materialTypeMTO?: boolean
  onClickDelete?: () => void
  onClickDuplicate?: () => void
  onClickPostOn?: (array: ClientsListInterface[]) => Promise<void>
  disableClientConfig?: boolean
  disableOrderConfig?: boolean
  disableFields?: boolean
  showDeleteModal?: boolean
  isButtonRescueData?: boolean
  disableToolbar?: boolean
  hasOvs?: boolean
  step?: number
  isEDI?: boolean
}

export interface PaymentMethodsProps {
  rowscount: number
  cd_cond_pgto: string
  nr_dia_limt: number
  ds_cond_pgto: string
  nr_dias_adic: number
}

export default function TopSection({
  setLoading = () => {
    return
  },
  quotasMonth = [],
  defaultPayment,
  defaultClient,
  orderNumber,
  materialTypeMTO,
  onClickDelete = () => {
    return
  },
  onClickDuplicate = () => {
    return
  },
  onClickPostOn,
  disableClientConfig = false,
  disableFields = false,
  disableOrderConfig = false,
  showDeleteModal = true,
  isButtonRescueData = false,
  disableToolbar = false,
  hasOvs = false,
  step,
  isEDI = false,
}: TopSectionProps) {
  const { setValue, getValues, formState, trigger, watch, reset } = useFormContext()
  const matchNewOrder = useMatch(URL_CONST.NEW_ORDERS)
  const { isDirty } = formState
  useNavBlocker(isDirty)

  const dt_ano_mes_cota = watch('dt_ano_mes_cota')

  const myClient = watch('cd_clie')

  const noClient = !Object.entries(getValues('cd_clie')).length

  const { errors } = formState

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { draftBody, isYPOP, isYASC, isSampleOrder } = useSelector((state) => state.salesOrder)

  const [isDisableButtonSave, setIsDisableButtonSave] = useState(false)
  const [addressOptions, setAddressOptions] = useState<OptionProps[]>([])
  const [inputAutocomplete, setInputAutocomplete] = useState('')
  const [inputAutocompleteSaleRegion, setInputAutocompleteSaleRegion] = useState('')
  const [clientOvTypes, setClientOvTypes] = useState<OptionProps[]>([])
  const [clientCredit, setClientCredit] = useState<ClientCrediInfo>({} as ClientCrediInfo)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsProps[]>([])
  const isMounted = useRef(false)

  const handleDelete = () => {
    onClickDelete()
    dispatch(setConfirmedNavigation(true))
    navigate(URL_CONST.ROOT, { replace: true })
    dispatch(setShowPrompt(false))
  }

  const isDisableButtonRescueData = !Object.entries(myClient)?.length
  const onClickRescueData = async () => {
    const query = {
      codigoCliente: myClient?.cd_clie ?? myClient,
      numeroPedido: getValues('nr_pedi_rasc'),
      preparacaoMto: getValues('fl_mto_prep') === TypesValidations.YES,
    }
    await getLastHeader(query)
      .then((response) => {
        if (response) {
          setValue('cd_tipo_orde', response.TipoOrdemVenda)
          if (response.CondicaoPagamento?.trim()) {
            setValue('cd_cond_pgto', response.CondicaoPagamento)
            const paymentOption = paymentMethods.find(
              (payment) => payment.cd_cond_pgto === response.CondicaoPagamento,
            )
            setValue('ds_cond_pgto', paymentOption?.ds_cond_pgto)
            setInputAutocomplete(`${response.CondicaoPagamento} - ${paymentOption?.ds_cond_pgto}`)
          }
          setValue('nr_dias_adic', response.PrazoExtra)
          const currentValue = getValues('pc_dsct') as unknown as number
          const pc_dsct = handleDiscountInput(response.Desconto, currentValue)
          setValue('pc_dsct', pc_dsct)
          setValue('ds_text_item_mto', response.TextoItemMto)
          setValue('fl_data_fixa_entr', response.FlagDataFixaEntrega ?? TypesValidations.NO)
        }
      })
      .catch(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (defaultPayment) setInputAutocomplete(defaultPayment)
    const getCondicoesPagamento = async () => {
      const returnApi = await getPaymentsTerms({
        cd_canl_dstb: '',
        cd_cond_pgto: '',
        cd_empr: '',
        cd_orgn_vend: '',
        cd_tipo_orde: '',
        fl_orde_tele_vend: TypesValidations.NO,
        qt_rowcount: 999,
      })

      if (returnApi) {
        localStorage.setItem('paymentMethods', JSON.stringify(returnApi))
        setPaymentMethods(returnApi)
      }
    }

    getCondicoesPagamento()

    if (defaultClient?.cd_clie) {
      const { cd_canl_dstb, cd_clie, cd_empr, cd_orgn_vend, cd_seto_atvd, cd_escr_vend } =
        defaultClient

      getClientAddress({
        cd_canl_dstb,
        cd_clie,
        cd_empr,
        cd_orgn_vend,
        cd_seto_atvd,
      }).then((response) => {
        setAddressOptions(
          response.map((add) => ({
            title: add.ds_endr,
            value: add.cd_pcro,
          })),
        )
      })

      // TODO: Check if necessary. Probably duplicated or bad location
      getClientOvTypes(cd_clie, cd_escr_vend).then((types) => {
        const ovTypes = types?.map((type: string) => {
          return { title: type, value: type }
        })

        setClientOvTypes(ovTypes)
      })
    }
    if (defaultClient?.cd_regi_vend && defaultClient?.ds_regi_vend) {
      setInputAutocompleteSaleRegion(
        `${defaultClient.cd_regi_vend} - ${defaultClient?.ds_regi_vend}`,
      )
    }

    const getCreditData = async () => {
      if (defaultClient?.cd_clie) {
        const credit = await getClientCredit(defaultClient.cd_clie)

        if (isMounted.current) {
          setClientCredit(credit)
        }
      }
    }

    getCreditData()
  }, [])

  const postDraft = async () => {
    setLoading(true)
    trigger()
    if (!isObjectEmpty(errors) && !matchNewOrder) {
      toast.error('Campos em vermelho são obrigatórios!')
      return
    }
    setIsDisableButtonSave(true)
    const form = getValues()
    const {
      cd_clie: {
        cd_canl_dstb,
        cd_clie,
        cd_empr,
        cd_equi_vend,
        cd_escr_vend,
        cd_orgn_vend,
        cd_regi_vend,
      },
      address,
      cd_seto_atvd,
      cd_tipo_orde,
      fl_mto_prep,
      pc_dsct,
      fl_dsct_matr,
      fl_prmt_desd,
      cd_cond_pgto,
      ds_cond_pgto,
      nr_pedi_clie,
      nr_dias_adic,
      cd_clie_entr,
      nr_pedi_rasc,
      ds_text_nota_fisc,
      fl_matr_fora_linh,
      dt_dsjd_pedi,
      ds_text_item_mto,
      cd_elem_pep,
      cd_cent_cust,
      cd_moti_prom,
      cd_catg_item,
      fl_data_fixa_entr,
      cd_mes_entr,
      fl_cbtr_camb,
      cd_inco,
      ds_armz,
      cd_mes_fatm,
      cd_mes_pedi,
    } = form

    const getQuotaMonth = async (): Promise<string> => {
      const quotaMonths = await getActiveQuotaMonths(cd_escr_vend)
      if (!quotaMonths.length) return ''

      return quotaMonths[0].Descricao
    }

    const currentQuotaMonth = await getQuotaMonth()

    const bodyParamsMaintainRascunho = {
      cd_canl_dstb: cd_canl_dstb || '',
      cd_catg_item,
      cd_cent_cust,
      cd_cent_prio: availableCentersFactories,
      cd_clie: cd_clie || '',
      cd_clie_entr: address || cd_clie_entr || '',
      cd_cond_pgto,
      cd_elem_pep,
      cd_empr: cd_empr || '',
      cd_equi_vend: cd_equi_vend || '',
      cd_escr_vend: cd_escr_vend || '',
      cd_inco,
      cd_mes_entr,
      cd_mes_fatm,
      cd_mes_pedi,
      cd_moti_prom,
      cd_orgn_vend: cd_orgn_vend || '',
      cd_regi_vend: cd_regi_vend || '',
      cd_seto_atvd: cd_seto_atvd || '',
      cd_tipo_orde,
      cd_usua_alte: getUser(),
      ds_armz,
      ds_cond_pgto,
      ds_item_pedi_rasc: [],
      ds_text_item_mto: ds_text_item_mto || '',
      ds_text_nota_fisc: ds_text_nota_fisc,
      dt_ano_mes_cota: dt_ano_mes_cota ? dt_ano_mes_cota : formatDate(currentQuotaMonth, 'YYYYMM'),
      dt_dsjd_pedi: dt_dsjd_pedi
        ? dt_dsjd_pedi
        : getLastDayOfMonth(currentQuotaMonth, 'YYYY-MM-DD'),
      fl_cbtr_camb,
      fl_data_fixa_entr,
      fl_dsct_matr: fl_dsct_matr,
      fl_matr_fora_linh: fl_matr_fora_linh || TypesValidations.NO,
      fl_mto_prep,
      fl_prmt_desd,
      nr_dias_adic,
      nr_pedi_clie,
      nr_pedi_cpra: '',
      nr_pedi_rasc: nr_pedi_rasc || '',
      pc_dsct,
    }

    await postMaintainDraft(bodyParamsMaintainRascunho)
      .then((response) => {
        if (response && response.nr_pedi_rasc) {
          const idDraft = response.nr_pedi_rasc.toString().trim()
          const successCallback = () =>
            matchNewOrder ? navigate(`${URL_CONST.ORDERS}/${idDraft}`) : setLoading(false)
          dispatch(setNumberDraft(idDraft))
          dispatch(asyncGetCorpoRascunho(idDraft, successCallback))
          setIsDisableButtonSave(false)
          toast.success('Pedido salvo com sucesso!')
          dispatch(getItems(''))
        }
      })
      .catch(() => {
        setLoading(false)
        setIsDisableButtonSave(false)
      })
    reset(getValues())
  }

  const renderSamplaOrderHeader = () => {
    if (isYPOP) {
      return (
        <NationalSampleHeader defaultCustomer={defaultClient} defaultPayment={inputAutocomplete} />
      )
    }

    if (isYASC) {
      return (
        <InternationalSampleHeader
          defaultCustomer={defaultClient}
          defaultPayment={inputAutocomplete}
        />
      )
    }

    return null
  }

  return (
    <>
      <ToolBar
        disableToolbar={disableToolbar}
        fullWidth={true}
        isButtonDelete={!hasOvs}
        isButtonDuplicate={!isSampleOrder}
        isButtonPendingEDI={isEDI}
        isButtonPostOn={!isSampleOrder}
        isButtonRescueData={!isSampleOrder && isButtonRescueData && !hasOvs}
        isButtonSave={!hasOvs && step === 0}
        isButtonTrack
        isDisableButtonDelete={!orderNumber || draftBody.cd_stat_pedi_rasc !== 0}
        isDisableButtonDuplicate={!orderNumber}
        isDisableButtonPostOn={!orderNumber}
        isDisableButtonRescueData={isDisableButtonRescueData}
        isDisableButtonSave={isDisableButtonSave || noClient || disableOrderConfig}
        isDisableButtonTrack={!orderNumber}
        materialTypeMTO={materialTypeMTO}
        onClickDelete={handleDelete}
        onClickDuplicate={onClickDuplicate}
        onClickPostOn={onClickPostOn}
        onClickRescueData={onClickRescueData}
        onClickSave={postDraft}
        orderNumber={orderNumber}
        showDeleteModal={showDeleteModal}
      />
      {isSampleOrder && !matchNewOrder ? (
        <SampleHeaderWrapper>{renderSamplaOrderHeader()}</SampleHeaderWrapper>
      ) : (
        <OVOrderHeader
          addressOptions={addressOptions}
          clientOvTypes={clientOvTypes} // TODO: Check if necessary
          defaultClient={defaultClient}
          disableClientConfig={disableClientConfig}
          disableFields={disableFields}
          disableOrderConfig={disableOrderConfig}
          hasOvs={hasOvs}
          inputAutocomplete={inputAutocomplete}
          inputAutocompleteSaleRegion={inputAutocompleteSaleRegion}
          paymentMethods={paymentMethods}
          postDraft={postDraft}
          quotasMonth={quotasMonth}
          setAddressOptions={setAddressOptions}
          setClientOvTypes={setClientOvTypes}
          setInputAutocomplete={setInputAutocomplete}
          setInputAutocompleteSaleRegion={setInputAutocompleteSaleRegion}
          setSubHeader={setClientCredit}
          subHeader={clientCredit}
          type={OVOrder.ORDER}
        />
      )}
    </>
  )
}
